import { useEffect, useState } from "react";
import { api, MXN, MONTH_NAMES, TRAMITES, TRAMITE_LIST } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronRight } from "lucide-react";

const YEARS = Array.from({ length: 11 }, (_, i) => 2026 + i);
const GRAY = "#9CA3AF";

export default function CobranzaChart({ onClose }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(YEARS.includes(currentYear) ? currentYear : 2026);
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      setData(null);
      try {
        const params = { year };
        if (month) params.month = month;
        const r = await api.get("/cobranza/summary", { params });
        setData(r.data);
      } catch {
        toast.error("Error al cargar la gráfica de cobranza");
      }
    })();
  }, [year, month]);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cobranza-chart-modal" className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Gráfica de cobranza</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {YEARS.map((y) => (
            <button
              key={y}
              data-testid={`cob-chart-year-${y}`}
              onClick={() => { setYear(y); setMonth(null); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                y === year ? "bg-black text-yellow-400 border-black" : "bg-white text-neutral-700 border-neutral-200 hover:border-yellow-500"
              }`}
            >{y}</button>
          ))}
        </div>

        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <button data-testid="cob-chart-back-year" onClick={() => setMonth(null)} className={`font-semibold ${!month ? "text-black" : "hover:text-black"}`}>{year}</button>
          {month && (<><ChevronRight className="w-4 h-4" /><span className="text-black font-semibold">{MONTH_NAMES[month - 1]}</span></>)}
        </div>

        {!data && <div className="text-neutral-500 text-sm py-10 text-center">Cargando…</div>}
        {data && !month && <YearChart data={data} onSelectMonth={setMonth} />}
        {data && month && <MonthChart data={data} month={month} />}
      </DialogContent>
    </Dialog>
  );
}

function YearChart({ data, onSelectMonth }) {
  const rows = data.months.map((m) => {
    const row = { name: MONTH_NAMES[m.month - 1].slice(0, 3), _month: m.month };
    for (const t of TRAMITE_LIST) row[t.label] = m.by_tramite?.[t.key] || 0;
    if (m.sin_tramite) row["Sin trámite"] = m.sin_tramite;
    return row;
  });
  return (
    <>
      <div className="rounded-2xl border border-yellow-500/40 p-4 bg-gradient-to-br from-black to-neutral-900 text-yellow-400">
        <div className="text-xs uppercase tracking-wider opacity-80">Cobranza total del año</div>
        <div data-testid="cob-chart-total-year" className="mt-1 text-2xl font-heading font-extrabold font-mono">{MXN(data.total_year)}</div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl p-4" data-testid="cob-chart-year">
        <h3 className="font-heading font-semibold text-neutral-800 mb-1">Cobranza por mes</h3>
        <p className="text-xs text-neutral-500 mb-3">Haz clic en una barra o en un mes para ver sus clientes.</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={rows} onClick={(s) => { const p = s?.activePayload?.[0]?.payload; if (p?._month) onSelectMonth(p._month); }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => MXN(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {TRAMITE_LIST.map((t) => (
                <Bar key={t.key} dataKey={t.label} stackId="a" fill={t.color} style={{ cursor: "pointer" }} />
              ))}
              <Bar dataKey="Sin trámite" stackId="a" fill={GRAY} style={{ cursor: "pointer" }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {data.months.map((m) => (
          <button
            key={m.month}
            data-testid={`cob-chart-month-${m.month}`}
            onClick={() => onSelectMonth(m.month)}
            className="bg-white border border-neutral-200 rounded-xl p-3 text-left hover:border-yellow-500 hover:shadow-md transition"
          >
            <div className="font-heading font-bold text-black text-sm">{MONTH_NAMES[m.month - 1]}</div>
            <div className="text-xs text-yellow-700 font-mono font-semibold mt-1">{MXN(m.total)}</div>
          </button>
        ))}
      </div>
    </>
  );
}

function MonthChart({ data, month }) {
  const clients = data.clients || [];
  const fmt = (d) => new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  const rows = clients.map((c) => ({
    name: c.nombre_completo.split(" ").slice(0, 2).join(" "),
    Cantidad: c.cantidad,
    color: c.tramite ? TRAMITES[c.tramite].color : GRAY,
    fecha: fmt(c.fecha_cobranza),
  }));
  return (
    <>
      <div className="rounded-2xl border border-yellow-500/40 p-4 bg-gradient-to-br from-black to-neutral-900 text-yellow-400">
        <div className="text-xs uppercase tracking-wider opacity-80">Cobranza de {MONTH_NAMES[month - 1]}</div>
        <div data-testid="cob-chart-total-month" className="mt-1 text-2xl font-heading font-extrabold font-mono">{MXN(data.month_total)}</div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl p-4" data-testid="cob-chart-month">
        <h3 className="font-heading font-semibold text-neutral-800 mb-1">Clientes de {MONTH_NAMES[month - 1]}</h3>
        <div className="flex flex-wrap gap-3 text-[11px] text-neutral-600 mb-3">
          {TRAMITE_LIST.map((t) => (
            <span key={t.key} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: t.color }} />{t.label}</span>
          ))}
        </div>
        {rows.length === 0 ? (
          <div className="text-center text-neutral-400 text-sm py-10">Sin cobranza en este mes.</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={rows.length > 6 ? -25 : 0} textAnchor={rows.length > 6 ? "end" : "middle"} height={rows.length > 6 ? 60 : 30} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => MXN(v)} labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.fecha || ""}`} />
                <Bar dataKey="Cantidad" radius={[6, 6, 0, 0]}>
                  {rows.map((r, i) => <Cell key={i} fill={r.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="cob-chart-clients-list">
        <div className="divide-y divide-neutral-100">
          {clients.map((c) => {
            const t = c.tramite ? TRAMITES[c.tramite] : null;
            return (
              <div key={c.id} data-testid={`cob-chart-client-${c.id}`} className="flex items-center gap-3 px-4 py-3">
                <span className="w-1.5 self-stretch rounded-full" style={{ backgroundColor: t?.color || GRAY }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900 truncate">{c.nombre_completo}</span>
                    {t && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                        style={{ backgroundColor: `${t.color}12`, color: t.color, borderColor: `${t.color}55` }}>{t.label}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono mt-1">NSS {c.nss} · {c.zona}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">Fecha de cobro</div>
                  <div className="text-sm text-neutral-800">{fmt(c.fecha_cobranza)}</div>
                </div>
                <div className="text-right min-w-[110px]">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">Cantidad</div>
                  <div className="font-mono font-semibold text-yellow-700">{MXN(c.cantidad)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
